import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit'
import {
  getTimekeepings,
  getTimekeeping,
  createTimekeeping,
  updateTimekeeping,
  deleteTimekeeping,
  softDeleteTimekeepings,
  restoreTimekeepings,
  permanentDeleteTimekeepings,
  getDatesSummary,
  getNearestDate,
  TimekeepingFilters,
} from './TimekeepingCRUD'
import { TimekeepingModel } from '../models/TimekeepingModel'

interface TimekeepingState {
  timekeepings: TimekeepingModel[]
  currentTimekeeping: TimekeepingModel | null
  loading: boolean
  error: string | null
  filters: TimekeepingFilters | null
}

const initialState: TimekeepingState = {
  timekeepings: [],
  currentTimekeeping: null,
  loading: false,
  error: null,
  filters: null,
}

const normalizeTimekeeping = (record: TimekeepingModel): TimekeepingModel => {
  const normalized = { ...(record as any) } as any
  const employeeRef = normalized.employee

  if (normalized.date) {
    normalized.date = String(normalized.date).split('T')[0]
  }

  if (!normalized.employee_id) {
    normalized.employee_id = typeof employeeRef === 'object'
      ? employeeRef?.id
      : employeeRef
  }

  return normalized as TimekeepingModel
}

const normalizeTimekeepingList = (payload: unknown): TimekeepingModel[] => {
  const data = payload as any
  const list = Array.isArray(data) ? data : (Array.isArray(data?.results) ? data.results : [])
  return list.map((item: TimekeepingModel) => normalizeTimekeeping(item))
}

// Async thunks
export const fetchTimekeepings = createAsyncThunk(
  'timekeepings/fetchTimekeepings',
  async (filters?: TimekeepingFilters) => {
    const response = await getTimekeepings(filters)
    return response.data
  }
)

export const fetchTimekeeping = createAsyncThunk(
  'timekeepings/fetchTimekeeping',
  async (id: string) => {
    const response = await getTimekeeping(id)
    return response.data
  }
)

export const addTimekeeping = createAsyncThunk(
  'timekeepings/addTimekeeping',
  async (timekeepingData: Partial<TimekeepingModel>) => {
    const response = await createTimekeeping(timekeepingData)
    return response.data
  }
)

export const updateTimekeepingAction = createAsyncThunk(
  'timekeepings/updateTimekeeping',
  async ({ id, data }: { id: string; data: Partial<TimekeepingModel> }) => {
    const response = await updateTimekeeping(id, data)
    return response.data
  }
)

export const removeTimekeeping = createAsyncThunk(
  'timekeepings/removeTimekeeping',
  async (id: string) => {
    await deleteTimekeeping(id)
    return id
  }
)

export const softDeleteTimekeepingsAction = createAsyncThunk(
  'timekeepings/softDeleteTimekeepings',
  async (ids: string[]) => {
    await softDeleteTimekeepings(ids)
    return ids
  }
)

export const restoreTimekeepingsAction = createAsyncThunk(
  'timekeepings/restoreTimekeepings',
  async (ids: string[]) => {
    await restoreTimekeepings(ids)
    return ids
  }
)

export const permanentDeleteTimekeepingsAction = createAsyncThunk(
  'timekeepings/permanentDeleteTimekeepings',
  async (ids: string[]) => {
    await permanentDeleteTimekeepings(ids)
    return ids
  }
)

export const fetchDatesSummary = createAsyncThunk(
  'timekeepings/fetchDatesSummary',
  async (params?: string | { month?: string; company?: string }) => {
    const month = typeof params === 'string' ? params : params?.month;
    const company = typeof params === 'object' && params ? params.company : undefined;
    const response = await getDatesSummary(month, company);
    return response.data;
  }
)

export const fetchNearestDate = createAsyncThunk(
  'timekeepings/fetchNearestDate',
  async () => {
    const response = await getNearestDate()
    return response.data
  }
)

const timekeepingSlice = createSlice({
  name: 'timekeepings',
  initialState,
  reducers: {
    setFilters: (state, action: PayloadAction<TimekeepingFilters | null>) => {
      state.filters = action.payload
    },
    clearCurrentTimekeeping: (state) => {
      state.currentTimekeeping = null
    },
    updateTimekeepingInList: (state, action: PayloadAction<TimekeepingModel>) => {
      const index = state.timekeepings.findIndex(t => t.id === action.payload.id)
      if (index !== -1) {
        state.timekeepings[index] = action.payload
      }
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch timekeepings
      .addCase(fetchTimekeepings.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(fetchTimekeepings.fulfilled, (state, action) => {
        state.loading = false
        state.timekeepings = normalizeTimekeepingList(action.payload)
      })
      .addCase(fetchTimekeepings.rejected, (state, action) => {
        state.loading = false
        state.error = action.error.message || 'Failed to fetch timekeepings'
      })
      // Fetch single timekeeping
      .addCase(fetchTimekeeping.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(fetchTimekeeping.fulfilled, (state, action) => {
        state.loading = false
        state.currentTimekeeping = action.payload
      })
      .addCase(fetchTimekeeping.rejected, (state, action) => {
        state.loading = false
        state.error = action.error.message || 'Failed to fetch timekeeping'
      })
      // Add timekeeping
      .addCase(addTimekeeping.fulfilled, (state, action) => {
        // Normalize date format và employee ID
        const payload = normalizeTimekeeping(action.payload as TimekeepingModel) as any;
        const requestData = action.meta.arg as any; // Get original request data
        
        if (payload.date) {
          payload.date = payload.date.split('T')[0]; // Ensure YYYY-MM-DD format
        }
        
        // Backend might not return employee field, so we need to get employee_id from request
        if (!payload.employee_id && requestData?.employee_id) {
          payload.employee_id = requestData.employee_id;
        }
        
        // Backend might not return employee field, so we need to preserve employee_id from request
        // If employee is an object, extract the ID
        if (payload.employee && typeof payload.employee === 'object') {
          payload.employee_id = payload.employee.id || payload.employee_id || requestData?.employee_id;
          payload.employee = payload.employee.id || payload.employee;
        } else if (!payload.employee && !payload.employee_id) {
          // If neither employee nor employee_id exists, get from request
          if (requestData?.employee_id) {
            payload.employee_id = requestData.employee_id;
            payload.employee = requestData.employee_id;
          }
        }
        
        // Ensure employee_id is set (for comparison)
        if (!payload.employee_id && payload.employee) {
          payload.employee_id = typeof payload.employee === 'string' ? payload.employee : (payload.employee as any)?.id;
        }
        
        // Ensure employee is set to employee_id if it's missing
        if (!payload.employee && payload.employee_id) {
          payload.employee = payload.employee_id;
        }
        
        // Check if record already exists (avoid duplicates)
        const existingIndex = state.timekeepings.findIndex(t => {
          const tEmpId = typeof t.employee === 'object' ? (t.employee as any)?.id : ((t as any).employee_id || t.employee);
          const tDate = t.date ? t.date.split('T')[0] : '';
          const pEmpId = payload.employee_id || payload.employee;
          const pDate = payload.date ? payload.date.split('T')[0] : '';
          return tEmpId === pEmpId && tDate === pDate;
        });
        
        if (existingIndex === -1) {
          state.timekeepings.unshift(payload);
        } else {
          // Update existing record instead
          state.timekeepings[existingIndex] = payload;
        }
      })
      // Update timekeeping
      .addCase(updateTimekeepingAction.fulfilled, (state, action) => {
        const payload = normalizeTimekeeping(action.payload as TimekeepingModel) as any;
        const requestData = action.meta.arg as any; // Get original request data { id, data }
        
        // Normalize date format và employee ID
        if (payload.date) {
          payload.date = payload.date.split('T')[0]; // Ensure YYYY-MM-DD format
        }
        
        // Backend might not return employee field, so we need to get employee_id from request
        if (!payload.employee_id && requestData?.data?.employee_id) {
          payload.employee_id = requestData.data.employee_id;
        }
        
        // Backend might not return employee field, so we need to preserve employee_id from request
        // If employee is an object, extract the ID
        if (payload.employee && typeof payload.employee === 'object') {
          payload.employee_id = payload.employee.id || payload.employee_id || requestData?.data?.employee_id;
          payload.employee = payload.employee.id || payload.employee;
        } else if (!payload.employee && !payload.employee_id) {
          // If neither employee nor employee_id exists, get from request
          if (requestData?.data?.employee_id) {
            payload.employee_id = requestData.data.employee_id;
            payload.employee = requestData.data.employee_id;
          }
        }
        
        // Ensure employee_id is set (for comparison)
        if (!payload.employee_id && payload.employee) {
          payload.employee_id = typeof payload.employee === 'string' ? payload.employee : (payload.employee as any)?.id;
        }
        
        // Ensure employee is set to employee_id if it's missing
        if (!payload.employee && payload.employee_id) {
          payload.employee = payload.employee_id;
        }
        
        const index = state.timekeepings.findIndex(t => t.id === payload.id);
        
        if (index !== -1) {
          state.timekeepings[index] = payload;
        } else {
          // If not found, add it (might be a new record)
          state.timekeepings.unshift(payload);
        }
        
        if (state.currentTimekeeping?.id === payload.id) {
          state.currentTimekeeping = payload;
        }
      })
      // Remove timekeeping
      .addCase(removeTimekeeping.fulfilled, (state, action) => {
        state.timekeepings = state.timekeepings.filter(t => t.id !== action.payload)
      })
      // Soft delete timekeepings
      .addCase(softDeleteTimekeepingsAction.fulfilled, (state, action) => {
        action.payload.forEach(id => {
          const timekeeping = state.timekeepings.find(t => t.id === id)
          if (timekeeping) {
            timekeeping.is_deleted = true
          }
        })
      })
      // Restore timekeepings
      .addCase(restoreTimekeepingsAction.fulfilled, (state, action) => {
        action.payload.forEach(id => {
          const timekeeping = state.timekeepings.find(t => t.id === id)
          if (timekeeping) {
            timekeeping.is_deleted = false
            timekeeping.deleted_at = null
            timekeeping.deleted_by = null
          }
        })
      })
      // Permanent delete timekeepings
      .addCase(permanentDeleteTimekeepingsAction.fulfilled, (state, action) => {
        state.timekeepings = state.timekeepings.filter(t => !action.payload.includes(t.id))
      })
  },
})

export const { setFilters, clearCurrentTimekeeping, updateTimekeepingInList } = timekeepingSlice.actions
export default timekeepingSlice.reducer

